import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import {
  Button,
  Checkbox,
  CheckboxSelector,
  ConfirmationPopup,
  FilterSelect,
  IconCheckmark,
  IconDisabled,
  IconFilter,
  IconSearch,
  List,
  Notification,
  Page,
  Selector,
  SimpleFilterTag,
  TextInput,
  DeviceIcon,
  DescriptionTable,
  Forbidden
} from 'shared/components'
import {
  defaultDateFormat,
  DevicesListColumns,
  initialDevicesListSort,
  initialLicenseListSize,
  initialLicenseListFilter,
  searchType,
  blacklistedFilterFields,
  platformFeatures
} from 'shared/constants'
import {
  capitalizeFirstLetter,
  displayValue,
  formatDate,
  getTableColumnObjects,
  getTableColumnValues,
  sendErrorReport,
  isFeatureAllowed
} from 'shared/helpers'
import { updateUIOptions } from 'src/license/actions'
import { updateUserPreferences } from 'src/company/actions'
import { transformSort, transformSearchFilter } from './helpers'
import {
  getDevices,
  bulkDeviceReset,
  bulkDeviceBlacklist,
  bulkDeviceWhitelist
} from 'src/devices/actions'
import BulkActionsDropdown from './BulkActionsDropdown'
import './styles.scss'

const AllDevices = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { companyId } = useParams()
  const uiOptions = useSelector(state => get(state, 'company.userPreferences.ui_options'))
  const userPrefsID = useSelector(state => get(state, 'company.userPreferences.id'))

  const [isLoading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [currentBulkAction, setCurrentBulkAction] = useState(null)
  const [confirmationPopupDisplayed, setConfirmationPopupDisplay] = useState(false)
  const [processingBulkAction, setProcessingBulkAction] = useState(false)
  // table state
  const [deviceCount, setDeviceCount] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(
    get(uiOptions, 'list_size.devices') || initialLicenseListSize
  )
  const [currentSort, setSort] = useState(
    get(uiOptions, 'list_sort.devices') || initialDevicesListSort
  )
  const [columnsToShow, setColumnsToShow] = useState(
    getTableColumnObjects(get(uiOptions, 'list_columns.devices'), DevicesListColumns)
  )
  const [changeColumnsDirty, setChangeColumnsDirty] = useState(false)
  const [tableExpanded, setTableExpanded] = useState({})
  // search state
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [searchBy, setSearchBy] = useState('search')
  // filters
  const [showFilters, setFiltersDisplay] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(initialLicenseListFilter)
  const [selectedBlacklistedFilter, setSelectedBlacklistedFilter] = useState(
    blacklistedFilterFields[0]
  )

  const fetchDevices = (
    tablePage = 0,
    activeQ = undefined,
    searchTypeSelect = undefined,
    sort = currentSort,
    rows = rowsPerPage,
    filters = selectedFilters
  ) => {
    setLoading(true)
    getDevices(
      companyId,
      tablePage,
      activeQ,
      transformSearchFilter(searchTypeSelect),
      transformSort(sort),
      rows,
      filters
    )
      .then(res => {
        setDevices(get(res, 'data.results') || [])
        setDeviceCount(get(res, 'data.count'))
        setLoading(false)
      })
      .catch(err => {
        setLoading(false)
        sendErrorReport(err, 'Cannot fetch devices list')
      })
  }

  useEffect(() => {
    fetchDevices()
  }, [])

  const patchUiFeatures = (
    newSorted = undefined,
    newPageSize = undefined,
    newFilters = undefined
  ) => {
    const sortObject = newSorted || currentSort
    const listPageSize = newPageSize || rowsPerPage
    const filters = newFilters || get(uiOptions, 'list_filters.devices') || {}

    if ((!changeColumnsDirty && !newSorted && !newPageSize && !newFilters) || !userPrefsID) {
      return false
    }

    const listColumns = get(uiOptions, 'list_columns') || {}
    const listSort = get(uiOptions, 'list_sort') || {}
    const listSize = get(uiOptions, 'list_size') || {}
    const listFilter = get(uiOptions, 'list_filters') || {}

    listColumns.devices = getTableColumnValues(columnsToShow)
    listSort.devices = sortObject
    listSize.devices = listPageSize
    listFilter.devices = filters

    const ui = {
      ...uiOptions,
      list_columns: listColumns,
      list_sort: listSort,
      list_size: listSize,
      list_filters: listFilter
    }
    const data = { ui_options: JSON.stringify(ui) }

    updateUIOptions(userPrefsID, companyId, data)
      .then(res => {
        dispatch(updateUserPreferences(res.data))
        setChangeColumnsDirty(false)
      })
      .catch(err => sendErrorReport(err, 'Ui options update failed', data))
    return true
  }

  const handleBulkDeviceOperation = async operation => {
    setProcessingBulkAction(true)

    // Map operation names to their corresponding action functions
    const actionMap = {
      blacklist: bulkDeviceBlacklist,
      whitelist: bulkDeviceWhitelist,
      reset: bulkDeviceReset
    }

    try {
      await actionMap[operation](companyId, selectedDevices)

      const selectedCount = selectedDevices.length
      const successMessages = {
        blacklist: __('Devices successfully blacklisted'),
        whitelist: __('Devices successfully whitelisted'),
        reset: __('Devices successfully reset')
      }
      const singleSuccessMessages = {
        blacklist: __('Device successfully blacklisted'),
        whitelist: __('Device successfully whitelisted'),
        reset: __('Device successfully reset')
      }

      // Refresh devices list
      fetchDevices(page, activeQuery, searchBy, currentSort, rowsPerPage, selectedFilters)

      // Reset states
      setSelectedDevices([])
      setProcessingBulkAction(false)
      setConfirmationPopupDisplay(false)

      // Show success notification
      Notification(
        'success',
        selectedCount > 1 ? successMessages[operation] : singleSuccessMessages[operation]
      )
    } catch (err) {
      sendErrorReport(err, `${capitalizeFirstLetter(operation)}ing devices failed`, selectedDevices)
      setProcessingBulkAction(false)
      Notification('error', __('Error occurred'), __('Your changes were not saved'))
    }
  }

  const blacklistSelectedDevices = () => handleBulkDeviceOperation('blacklist')
  const whitelistSelectedDevices = () => handleBulkDeviceOperation('whitelist')
  const resetSelectedDevices = () => handleBulkDeviceOperation('reset')

  const handleBulkAction = actionId => {
    setCurrentBulkAction(actionId)
    setConfirmationPopupDisplay(true)
  }

  const executeBulkAction = () => {
    switch (currentBulkAction) {
      case 'blacklist':
        return blacklistSelectedDevices()
      case 'whitelist':
        return whitelistSelectedDevices()
      case 'reset':
        return resetSelectedDevices()
      default:
        return null
    }
  }

  const handleSearchSubmit = e => {
    e.preventDefault()
    setActiveQuery(query)
    setPage(0)
    fetchDevices(0, query, searchBy, currentSort, rowsPerPage, selectedFilters)
  }

  const handleSearchClear = () => {
    setQuery('')
    setActiveQuery('')
    setPage(0)
    fetchDevices(0, undefined, undefined, currentSort, rowsPerPage, selectedFilters)
  }

  const handleClearFilters = () => {
    const newFilters = {
      blacklisted: blacklistedFilterFields[0]
    }
    setSelectedFilters(newFilters)
    setSelectedBlacklistedFilter(blacklistedFilterFields[0])
    fetchDevices(0, activeQuery, searchBy, currentSort, rowsPerPage, newFilters)
    patchUiFeatures(undefined, undefined, newFilters)
    return true
  }

  const handleBlacklistedFilterSelect = val => {
    setSelectedBlacklistedFilter(val)
    const newFilters = {
      blacklisted: val
    }
    setSelectedFilters(newFilters)
    fetchDevices(0, activeQuery, searchBy, currentSort, rowsPerPage, newFilters)
    patchUiFeatures(undefined, undefined, newFilters)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    fetchDevices(newPage, activeQuery, searchBy, currentSort, rowsPerPage, selectedFilters)
  }

  const handleSortChange = newSorted => {
    setSort(newSorted)
    fetchDevices(0, activeQuery, searchBy, newSorted, rowsPerPage, selectedFilters)
    patchUiFeatures(newSorted)
  }

  const handlePageSizeChange = (newPageSize, newPage) => {
    setPage(newPage)
    setRowsPerPage(newPageSize)
    fetchDevices(newPage, activeQuery, searchBy, currentSort, newPageSize, selectedFilters)
    patchUiFeatures(undefined, newPageSize)
  }

  const shouldShowColumn = name => {
    const values = columnsToShow.map(column => column.value)
    return !!values.includes(name)
  }

  const redirectToDevicePage = rowData => {
    const deviceId = get(rowData, 'original.id')
    history.push(`/${companyId}/devices/${deviceId}`)
  }

  const isAllowed = isFeatureAllowed(platformFeatures.reporting_devices_view)
  if (!isAllowed) {
    return (
      <Page title={__('Devices')}>
        <div className='DeviceList'>
          <Forbidden
            text={__('This feature is not available in your current plan type')}
            description={__('Contact sales if you wish to change this policy.')}
          />
        </div>
      </Page>
    )
  }

  return (
    <Page title={__('Devices')}>
      <div className='DeviceList'>
        <div className='list-header'>
          <div>
            <div className='search-selector'>
              <Selector
                handleChange={val => setSearchBy(val)}
                options={[
                  { label: __('Search by all'), value: 'search' },
                  {
                    label: __('Search by exact device hardware_id'),
                    value: searchType.hardwareID
                  },
                  {
                    label: __('Search by exact device hostname'),
                    value: searchType.deviceHostname
                  },
                  {
                    label: __('Search by exact device OS'),
                    value: searchType.os
                  }
                ]}
                value={searchBy}
              />
            </div>
            <form onSubmit={handleSearchSubmit} style={{ height: '34px' }}>
              <TextInput handleChange={val => setQuery(val)} value={query} />
              {activeQuery && (
                <button type='button' onClick={handleSearchClear} disabled={isLoading}>
                  &times;
                </button>
              )}
              <Button type='submit' theme='info' disabled={isLoading}>
                <IconSearch fill='#fff' />
              </Button>
            </form>
            <div className='columns-display-select'>
              <CheckboxSelector
                text={__('Table columns')}
                options={DevicesListColumns}
                value={columnsToShow}
                onChangeCallback={data => {
                  setChangeColumnsDirty(true)
                  setColumnsToShow(data)
                }}
                onMenuClose={patchUiFeatures}
              />
            </div>
          </div>
        </div>
        <div className='LicenseList-filters'>
          <button
            type='button'
            className='AdvancedSearch-btn'
            onClick={() => setFiltersDisplay(!showFilters)}
          >
            <IconFilter stroke='#2e86de' strokeWidth={2} fill='none' viewBox='0 0 24 24' />
            {__('Search filters')}
          </button>
          <div className={`AdvancedSearch-wrapper ${showFilters ? '' : 'hide'}`}>
            <div className={`AdvancedSearch ${showFilters ? '' : 'hide'}`}>
              <div className='AdvancedSearch-fields'>
                <FilterSelect
                  filter={selectedBlacklistedFilter}
                  filterArray={blacklistedFilterFields}
                  filterSetter={handleBlacklistedFilterSelect}
                  label={__('Is blacklisted')}
                  labelID='license-usage-filter'
                  isDisabled={isLoading}
                />
                <div className='AdvancedSearch-actions'>
                  <Button theme='error' size='sm' disabled={isLoading} onClick={handleClearFilters}>
                    {__('Clear filters')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className='Filters-list'>
            <SimpleFilterTag
              label={selectedBlacklistedFilter.label}
              value={selectedBlacklistedFilter.value}
              filtersArray={blacklistedFilterFields}
              filterSetter={handleBlacklistedFilterSelect}
              isDisabled={isLoading}
            />
          </div>
        </div>
        <BulkActionsDropdown
          disabled={!selectedDevices.length}
          onActionSelect={handleBulkAction}
          style={{ marginBottom: '1rem' }}
        />
        <List
          clickable
          data={devices}
          onExpandedChange={expanded => setTableExpanded(expanded)}
          expanded={tableExpanded}
          SubComponent={row => {
            const hasUsers =
              get(row, 'original.license_users') && get(row, 'original.license_users').length > 0
            const isBorrowed = get(row, 'original.borrowed_until')
            return (
              <div className='SubComponent'>
                <DescriptionTable
                  details={[
                    {
                      label: isBorrowed ? __('Borrowed until') : null,
                      value: formatDate(get(row, 'original.borrowed_until'), defaultDateFormat)
                    },
                    {
                      label: __('First activated'),
                      value: formatDate(get(row, 'original.time_activated'), defaultDateFormat)
                    },
                    {
                      label: __('OS'),
                      value: displayValue(get(row, 'original.os'))
                    },
                    {
                      label: __('SDK build'),
                      value: displayValue(get(row, 'original.sdk_build_version'))
                    },
                    {
                      label: __('App version'),
                      value: displayValue(get(row, 'original.app_ver'))
                    },
                    {
                      label: __('Hostname'),
                      value: displayValue(get(row, 'original.hostname'))
                    },
                    {
                      label: __('External IP address'),
                      value: displayValue(get(row, 'original.external_ip'))
                    },
                    {
                      label: __('IP address'),
                      value: displayValue(get(row, 'original.ip'))
                    },
                    {
                      label: __('MAC address'),
                      value: displayValue(get(row, 'original.mac_address'))
                    },
                    {
                      label: __('Is virtual machine'),
                      value: get(row, 'original.is_vm') ? __('Yes') : __('No')
                    },
                    {
                      label: __('VM info'),
                      value: displayValue(get(row, 'original.vm_info'))
                    }
                  ]}
                />
                {hasUsers && (
                  <div className='SubComponent-users'>
                    <div className='heading'>{__('Users')}</div>
                    <ul className='list'>
                      {get(row, 'original.license_users').map(u => (
                        <li key={u.id}>{u.true_email}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )
          }}
          columns={[
            {
              id: 'select',
              className: 'select-container',
              Cell: rowData => (
                <button
                  className='table-select'
                  aria-label='Select device'
                  onClick={e => {
                    e.stopPropagation()
                    let newState
                    const isSelected = selectedDevices.includes(rowData.original.id)
                    if (isSelected) {
                      newState = selectedDevices.filter(l => l !== rowData.original.id)
                    } else {
                      newState = selectedDevices.concat(rowData.original.id)
                    }
                    setSelectedDevices(newState)
                  }}
                  type='button'
                >
                  <Checkbox
                    inputId={`${rowData.original.id}-select`}
                    handleChange={(val, e) => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                    checked={selectedDevices.includes(rowData.original.id)}
                  />
                </button>
              ),
              width: 60,
              show: true,
              sortable: false
            },
            {
              expander: true,
              Header: __('Details'),
              headerClassName: 'text-center',
              width: 80,
              style: {
                fontSize: 25,
                padding: '0',
                textAlign: 'center',
                userSelect: 'none'
              }
            },
            {
              accessor: 'type',
              Header: __('Type'),
              className: 'text-center',
              headerClassName: 'text-center',
              width: 70,
              Cell: cellInfo => (
                <div className='device-type-icon'>
                  <DeviceIcon device={cellInfo.value} />
                </div>
              )
            },
            {
              accessor: 'hardware_id',
              Header: __('Hardware ID'),
              Cell: rowData => {
                return displayValue(rowData.value)
              },
              maxWidth: 200,
              show: shouldShowColumn('hardware_id')
            },
            {
              accessor: 'last_check',
              Header: __('Last Check'),
              maxWidth: 200,
              Cell: rowData => {
                const isBundle = get(rowData, 'original.is_bundle')
                if (isBundle) return '-'
                const isEnabled = get(rowData, 'original.enabled')
                if (!isEnabled) {
                  return '-'
                }
                return formatDate(rowData.value, defaultDateFormat)
              },
              show: shouldShowColumn('last_check')
            },
            {
              accessor: 'device_active',
              className: 'text-center',
              Header: __('Status'),
              headerClassName: 'text-center',
              Cell: rowData => {
                return rowData.value ? (
                  <IconCheckmark color='#10ac84' height='14px' />
                ) : (
                  <IconDisabled color='#aaa' height='14px' />
                )
              },
              width: 80,
              show: shouldShowColumn('device_active')
            },
            {
              accessor: 'device_variables',
              className: 'text-center',
              Header: __('Device Variables'),
              headerClassName: 'text-center',
              Cell: cellInfo => displayValue(JSON.stringify(cellInfo.value)),
              minWidth: 100,
              show: shouldShowColumn('device_variables')
            },
            {
              accessor: 'floating_in_use',
              Header: __('Floating in use'),
              maxWidth: 150,
              Cell: rowData => {
                return rowData.value ? (
                  <IconCheckmark color='#10ac84' height='14px' />
                ) : (
                  <IconDisabled color='#aaa' height='14px' />
                )
              },
              show: shouldShowColumn('floating_in_use')
            },
            {
              accessor: 'borrowed_until',
              Header: __('Borrowed'),
              Cell: rowData => {
                return rowData.value ? (
                  <IconCheckmark color='#10ac84' height='14px' />
                ) : (
                  <IconDisabled color='#aaa' height='14px' />
                )
              },
              show: shouldShowColumn('borrowed_until')
            },
            {
              accessor: 'floating_last_seen',
              className: 'text-center',
              Header: __('Floating last check'),
              headerClassName: 'text-center',
              Cell: rowData => {
                return rowData.value ? (
                  <IconCheckmark color='#10ac84' height='14px' />
                ) : (
                  <IconDisabled color='#aaa' height='14px' />
                )
              },
              minWidth: 100,
              show: shouldShowColumn('floating_last_seen')
            }
          ]}
          page={page}
          pages={Math.ceil(deviceCount / rowsPerPage)}
          loading={isLoading}
          manual
          minRows={get(devices, 'length') || 10}
          showPagination={deviceCount > 5}
          onPageChange={handlePageChange}
          onSortedChange={handleSortChange}
          defaultSorted={currentSort}
          handleClick={rowData => redirectToDevicePage(rowData)}
          showPageSizeOptions
          pageSize={rowsPerPage}
          onPageSizeChange={(pageSize, pageIndex) => handlePageSizeChange(pageSize, pageIndex)}
        />
      </div>
      {confirmationPopupDisplayed && (
        <ConfirmationPopup
          closeCb={() => setConfirmationPopupDisplay(false)}
          confirmCb={executeBulkAction}
          confirmText={capitalizeFirstLetter(currentBulkAction)}
          disabled={processingBulkAction}
          theme='error'
          title={__(`Are you sure you want to ${currentBulkAction} selected devices?`)}
        />
      )}
    </Page>
  )
}

export default AllDevices
